/**
 * Analytics Hook
 *
 * Integrates Google Analytics 4, Google Tag Manager, and Facebook Pixel
 * based on admin-configured settings.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalyticsSettings } from '../contexts/SettingsContext';
import type { Product } from '@petmas/shared';

// Extend window for analytics globals
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: (...args: unknown[]) => void;
  }
}

/**
 * Initialize and use analytics tracking
 */
export function useAnalytics() {
  const settings = useAnalyticsSettings();
  const location = useLocation();
  const initializedRef = useRef({
    ga4: false,
    gtm: false,
    fbPixel: false,
  });

  // Initialize GA4
  useEffect(() => {
    if (!settings.ga4Enabled || !settings.ga4MeasurementId || initializedRef.current.ga4) {
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', settings.ga4MeasurementId, {
      send_page_view: false, // We'll track manually for SPA
    });

    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${settings.ga4MeasurementId}`;
    document.head.appendChild(script);

    initializedRef.current.ga4 = true;

    return () => {
      // Cleanup not needed - scripts persist
    };
  }, [settings.ga4Enabled, settings.ga4MeasurementId]);

  // Initialize GTM
  useEffect(() => {
    if (!settings.gtmEnabled || !settings.gtmContainerId || initializedRef.current.gtm) {
      return;
    }

    // Initialize dataLayer if not already
    window.dataLayer = window.dataLayer || [];

    // GTM snippet
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${settings.gtmContainerId}');
    `;
    document.head.appendChild(script);

    // Add noscript fallback to body
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${settings.gtmContainerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    initializedRef.current.gtm = true;
  }, [settings.gtmEnabled, settings.gtmContainerId]);

  // Initialize Facebook Pixel
  useEffect(() => {
    if (!settings.fbPixelEnabled || !settings.fbPixelId || initializedRef.current.fbPixel) {
      return;
    }

    // FB Pixel snippet
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${settings.fbPixelId}');
    `;
    document.head.appendChild(script);

    // Add noscript pixel
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${settings.fbPixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    initializedRef.current.fbPixel = true;
  }, [settings.fbPixelEnabled, settings.fbPixelId]);

  // Track page views on route change
  useEffect(() => {
    const pagePath = location.pathname + location.search;
    const pageTitle = document.title;

    // GA4 page view
    if (settings.ga4Enabled && settings.ga4MeasurementId && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
      });
    }

    // GTM page view (via dataLayer)
    if (settings.gtmEnabled && settings.gtmContainerId && window.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
        page: {
          path: pagePath,
          title: pageTitle,
        },
      });
    }

    // FB Pixel page view
    if (settings.fbPixelEnabled && settings.fbPixelId && window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location, settings]);

  // Track custom events
  const trackEvent = useCallback(
    (eventName: string, params?: Record<string, unknown>) => {
      // GA4
      if (settings.ga4Enabled && window.gtag) {
        window.gtag('event', eventName, params);
      }

      // GTM (via dataLayer)
      if (settings.gtmEnabled && window.dataLayer) {
        window.dataLayer.push({
          event: eventName,
          ...params,
        });
      }
    },
    [settings.ga4Enabled, settings.gtmEnabled]
  );

  // Track FB Pixel events
  const trackFBEvent = useCallback(
    (eventName: string, params?: Record<string, unknown>) => {
      if (settings.fbPixelEnabled && window.fbq) {
        window.fbq('track', eventName, params);
      }
    },
    [settings.fbPixelEnabled]
  );

  // E-commerce: View product
  const trackViewProduct = useCallback(
    (product: Product) => {
      const params = {
        currency: 'PEN',
        value: product.price,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_brand: product.brand,
            item_category: product.category,
            price: product.price,
            quantity: 1,
          },
        ],
      };

      trackEvent('view_item', params);
      trackFBEvent('ViewContent', {
        content_ids: [product.id],
        content_type: 'product',
        content_name: product.name,
        value: product.price,
        currency: 'PEN',
      });
    },
    [trackEvent, trackFBEvent]
  );

  // E-commerce: Add to cart
  const trackAddToCart = useCallback(
    (product: Product, quantity: number) => {
      const params = {
        currency: 'PEN',
        value: product.price * quantity,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_brand: product.brand,
            item_category: product.category,
            price: product.price,
            quantity,
          },
        ],
      };

      trackEvent('add_to_cart', params);
      trackFBEvent('AddToCart', {
        content_ids: [product.id],
        content_type: 'product',
        content_name: product.name,
        value: product.price * quantity,
        currency: 'PEN',
      });
    },
    [trackEvent, trackFBEvent]
  );

  // E-commerce: Remove from cart
  const trackRemoveFromCart = useCallback(
    (product: Product, quantity: number) => {
      trackEvent('remove_from_cart', {
        currency: 'PEN',
        value: product.price * quantity,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_brand: product.brand,
            item_category: product.category,
            price: product.price,
            quantity,
          },
        ],
      });
    },
    [trackEvent]
  );

  // E-commerce: Begin checkout
  const trackBeginCheckout = useCallback(
    (items: Array<{ product: Product; quantity: number }>, total: number) => {
      const gaItems = items.map(({ product, quantity }) => ({
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        price: product.price,
        quantity,
      }));

      trackEvent('begin_checkout', {
        currency: 'PEN',
        value: total,
        items: gaItems,
      });

      trackFBEvent('InitiateCheckout', {
        content_ids: items.map((i) => i.product.id),
        content_type: 'product',
        value: total,
        currency: 'PEN',
        num_items: items.reduce((acc, i) => acc + i.quantity, 0),
      });
    },
    [trackEvent, trackFBEvent]
  );

  // E-commerce: Purchase
  const trackPurchase = useCallback(
    (
      orderId: string,
      items: Array<{ product: Product; quantity: number }>,
      total: number,
      shipping?: number,
      tax?: number
    ) => {
      const gaItems = items.map(({ product, quantity }) => ({
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        price: product.price,
        quantity,
      }));

      trackEvent('purchase', {
        transaction_id: orderId,
        currency: 'PEN',
        value: total,
        shipping,
        tax,
        items: gaItems,
      });

      trackFBEvent('Purchase', {
        content_ids: items.map((i) => i.product.id),
        content_type: 'product',
        value: total,
        currency: 'PEN',
        num_items: items.reduce((acc, i) => acc + i.quantity, 0),
      });
    },
    [trackEvent, trackFBEvent]
  );

  // Search
  const trackSearch = useCallback(
    (searchTerm: string) => {
      trackEvent('search', { search_term: searchTerm });
      trackFBEvent('Search', { search_string: searchTerm });
    },
    [trackEvent, trackFBEvent]
  );

  return {
    trackEvent,
    trackFBEvent,
    trackViewProduct,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackPurchase,
    trackSearch,
  };
}

/**
 * Hook to initialize analytics on app load
 * Use this in the root App component
 */
export function useAnalyticsInit() {
  // This just triggers the initialization
  useAnalytics();
}
